import { useCallback, useMemo, useState } from 'react'
import { Card, CardBody, IconButton, Typography } from '@material-tailwind/react'
import { PiTrash } from 'react-icons/pi'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import DeletePatternDialog from './DeletePatternDialog'
import { formatDateString } from '../utils/helpers/dates.helpers'
import { AGAMI_GLOBAL_EXPERIENCE_ID } from '../utils/constants/db.constants'
import { AgamiExperience, AgamiPattern } from '../types/supabase'

interface PatternPreviewCardProps {
  pattern: AgamiPattern
  experienceList: AgamiExperience[]
  refetch: () => void
}

const PatternPreviewCard: React.FC<PatternPreviewCardProps> = ({ pattern, experienceList, refetch }) => {
  const { t } = useTranslation()
  const experience = experienceList.find((exp: AgamiExperience) => exp.id === pattern.experience_id)

  const isAgamiGlobal = pattern.experience_id === AGAMI_GLOBAL_EXPERIENCE_ID

  const patternExpName = useMemo(() => {
    if (experience?.name) return experience.name
    if (isAgamiGlobal) return 'Agami Global'
    return '-'
  }, [isAgamiGlobal, experience])

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const manageDelete = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setDeleteDialogOpen(true)
  }, [])

  const navigate = useNavigate()

  return (
    <>
      <DeletePatternDialog
        isOpen={deleteDialogOpen}
        setIsOpen={setDeleteDialogOpen}
        pattern={pattern}
        refetch={refetch}
      />

      <Card
        className="w-full h-full cursor-pointer hover:bg-base-gray-400"
        onClick={() => navigate(`/patterns/${pattern.pattern_id}`)}
        data-testclass="pattern-preview-card"
      >
        <CardBody className="min-w-0">
          <div className="flex space-x-2 justify-between items-start">
            <div className="flex flex-col flex-1 gap-2 min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                {pattern.color && (
                  <div className="w-5 h-5 rounded-full shrink-0" style={{ backgroundColor: pattern.color }} />
                )}
                <Typography
                  className="font-bold truncate"
                  title={pattern.name}
                  data-testclass="pattern-preview-card-name"
                >
                  {pattern.name}
                </Typography>
              </div>

              <Typography variant="small">
                {t('dashboard.experience')}{' '}
                <strong className="text-black dark:text-base-gray-400">{patternExpName}</strong>
              </Typography>

              <Typography variant="small">
                {t('dashboard.created')}{' '}
                <strong className="text-black dark:text-base-gray-400">
                  {pattern.created_at ? formatDateString(pattern.created_at) : '-'}
                </strong>
              </Typography>
            </div>

            <IconButton
              variant="text"
              size="sm"
              color="yellow"
              onClick={manageDelete}
              data-testid="pattern-delete-btn"
            >
              <PiTrash className="text-primary dark:text-base-gray-500 text-[20px]" />
            </IconButton>
          </div>
        </CardBody>
      </Card>
    </>
  )
}

export default PatternPreviewCard
